import { Vector2Tuple, Vector2 } from 'three';
import { DrawingLine } from '../objects/DrawingLine';
import { DrawingVertex } from '../objects/DrawingVertex';
import { ProjectRenderer } from '../ProjectRenderer';
import { RendererMode, ThreeEvent } from '../types';
import { DrawingVertexList } from '../utils/DrawingVertexList';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export class DrawingManager {
	vertexList = new DrawingVertexList();
	lines: DrawingLine[] = [];

	constructor(private renderer: ProjectRenderer) {}

	private getLine(vert1: DrawingVertex, vert2: DrawingVertex) {
		const match = this.lines.find((line) => {
			return (
				(line.start === vert1 && line.end === vert2) ||
				(line.start === vert2 && line.end === vert1)
			);
		});

		if (!match) throw new Error('LINE_NOT_FOUND');
		return match;
	}

	private detectNearClosing() {
		if (this.vertexList.length < 3) return;
		if (!this.vertexList.last || !this.vertexList.first) return;

		if (
			this.vertexList.last.position.distanceTo(this.vertexList.first.position) >
			RENDERER_CONFIG.DRAWING_VERTEX.CLOSE_THRESHOLD
		)
			return;

		this.vertexList.last.position = this.vertexList.first.position.clone();
	}

	private tryCloseShape() {
		if (this.vertexList.length < 4) return false;
		if (!this.vertexList.last || !this.vertexList.first) return false;

		if (
			this.vertexList.last.position.distanceTo(
				this.vertexList.first.position
			) !== 0
		)
			return false;

		return true;
	}

	private addLine(vert1: DrawingVertex, vert2: DrawingVertex) {
		const playgroundLine = new DrawingLine(this.renderer, vert1, vert2);
		this.lines.push(playgroundLine);
	}

	private finishDrawing() {
		this.renderer.mode = RendererMode.ROOM;
		this.renderer.roomManager.setFromShape(this.vertexList.exportVertices());

		this.renderer.emitter.emit(ThreeEvent.UPDATE_OPTIONS, {
			isDrawing: false,
		});
	}

	handleMouseDown(pos: Vector2Tuple) {
		const tempVertex = this.vertexList.last;

		if (!tempVertex) return;
		if (tempVertex.isError) return;

		const prev = tempVertex.prev;
		if (prev && prev.position.equals(tempVertex.position)) return;

		tempVertex.isMovable = false;

		if (this.tryCloseShape()) {
			this.vertexList.remove(tempVertex);
			this.vertexList.closeLoop();

			this.finishDrawing();

			return;
		}

		const newVert = new DrawingVertex(this.renderer, pos);
		newVert.isMovable = true;
		this.vertexList.add(newVert);

		this.addLine(tempVertex, newVert);

		if (!tempVertex.prev) return;

		const oldLine = this.getLine(tempVertex.prev!, tempVertex);
		if (!oldLine) return;

		oldLine.updateMaterial();
	}

	handleMouseMove(pos: Vector2Tuple) {
		const vertex = this.vertexList.last;
		if (!vertex) return;

		if (vertex.prev) {
			const [px, py] = vertex.prev.position.toArray();
			const [x, y] = pos;
			const dx = Math.abs(x - px);
			const dy = Math.abs(y - py);

			if (dx > dy) vertex.position = new Vector2(x, py);
			else vertex.position = new Vector2(px, y);
		} else vertex.position = new Vector2(...pos);

		if (!vertex.prev) return;
		this.detectNearClosing();

		const line = this.getLine(vertex.prev, vertex);
		if (!line) return;

		line.checkIntersectionsWithOthers(this.lines);
	}

	initDrawingShapeMode() {
		this.renderer.emitter.emit(ThreeEvent.UPDATE_OPTIONS, {
			roomSurfaceArea: 0,
			isDrawing: true,
		});

		this.renderer.roomManager.dispose();
		this.renderer.mode = RendererMode.CUSTOM_SHAPE_DRAWING;
		const movableVertex = new DrawingVertex(this.renderer, [0, 0]);
		movableVertex.isMovable = true;
		this.vertexList.add(movableVertex);
	}

	resetShape() {
		this.dispose();
		this.initDrawingShapeMode();
	}

	dispose() {
		this.vertexList.clear();

		for (const line of this.lines) {
			line.dispose();
		}

		this.lines = [];
	}
}
